import { Action } from 'store/store';
import omitUndefined from 'utils/omitUndefined';
import { StreamError } from 'streamsState/store/streamStatuses/StreamErrorState';
import { ChannelDestination } from 'communication/Channel';
import { StreamElementDataMessage } from 'communication/types/StreamElement';
import { StreamedDataState } from 'streamsState/store/streamedData/StreamedDataState';

export enum StreamActionType {
    STREAM_RECEIVE_STREAM_MESSAGE = 'STREAM_RECEIVE_STREAM_MESSAGE',
    STREAM_RECEIVE_STREAM_MESSAGE_BATCH = 'STREAM_RECEIVE_STREAM_MESSAGE_BATCH',
    STREAM_CLEAR_STREAM_DATA = 'STREAM_CLEAR_STREAM_DATA',
    STREAM_SET_STREAM_DATA = 'STREAM_SET_STREAM_DATA',
    STREAM_HANDLE_STREAM_ERROR = 'STREAM_HANDLE_STREAM_ERROR',
    STREAM_REMOVE_STREAM_DATA = 'STREAM_REMOVE_STREAM_DATA',
    REPLACE_ALL_STREAM_DATA = 'REPLACE_ALL_STREAM_DATA',
}

export interface StreamActionPayload {
    channel: ChannelDestination;
    streamKey: string;
}

export interface StreamMessageReceivePayload<TDataElement>
    extends StreamActionPayload {
    message: StreamElementDataMessage<TDataElement>;
}

export type StreamMessageReceiveAction<TDataElement> = Action<
    StreamActionType.STREAM_RECEIVE_STREAM_MESSAGE,
    StreamMessageReceivePayload<TDataElement>
>;

export function createReceiveMessageAction<TDataElement>(
    channel: ChannelDestination,
    streamKey: string,
    message: StreamElementDataMessage<TDataElement>,
    allowMultiStream?: boolean
): StreamMessageReceiveAction<TDataElement> {
    if (!streamKey) {
        throw new Error('No stream key received');
    }
    return {
        type: StreamActionType.STREAM_RECEIVE_STREAM_MESSAGE,
        payload: omitUndefined({
            channel,
            streamKey,
            message,
            allowMultiStream,
        }),
    };
}

export interface StreamMessageReceiveBatchPayload<TDataElement>
    extends StreamActionPayload {
    messages: StreamElementDataMessage<TDataElement>[];
}

export type StreamMessageReceiveBatchAction<TDataElement> = Action<
    StreamActionType.STREAM_RECEIVE_STREAM_MESSAGE_BATCH,
    StreamMessageReceiveBatchPayload<TDataElement>
>;

export function createReceiveMessageBatchAction<TDataElement>(
    channel: ChannelDestination,
    streamKey: string,
    messages: StreamElementDataMessage<TDataElement>[],
    allowMultiStream?: boolean
): StreamMessageReceiveBatchAction<TDataElement> {
    if (!streamKey) {
        throw new Error('No stream key received');
    }
    return {
        type: StreamActionType.STREAM_RECEIVE_STREAM_MESSAGE_BATCH,
        payload: omitUndefined({
            channel,
            streamKey,
            messages,
            allowMultiStream,
        }),
    };
}

export type ClearStreamDataPayload = StreamActionPayload;
export type ClearStreamDataAction = Action<
    StreamActionType.STREAM_CLEAR_STREAM_DATA,
    ClearStreamDataPayload
>;

export function createClearStreamDataAction(
    channel: ChannelDestination,
    streamKey: string
): ClearStreamDataAction {
    return {
        type: StreamActionType.STREAM_CLEAR_STREAM_DATA,
        payload: { channel, streamKey },
    };
}

export type RemoveStreamDataActionPayload = StreamActionPayload;
export type RemoveStreamDataAction = Action<
    StreamActionType.STREAM_REMOVE_STREAM_DATA,
    RemoveStreamDataActionPayload
>;

export function createRemoveStreamDataAction(
    channel: ChannelDestination,
    streamKey: string
): RemoveStreamDataAction {
    return {
        type: StreamActionType.STREAM_REMOVE_STREAM_DATA,
        payload: { channel, streamKey },
    };
}

export interface SetStreamDataPayload extends StreamActionPayload {
    data: unknown;
}

export type SetStreamDataAction = Action<
    StreamActionType.STREAM_SET_STREAM_DATA,
    SetStreamDataPayload
>;

/*
 * Replaces the stream data completely. You will not normally use this. It was added for the support visualisation tool
 */
export function createSetStreamDataAction(
    channel: ChannelDestination,
    streamKey: string,
    data: unknown
): SetStreamDataAction {
    return {
        type: StreamActionType.STREAM_SET_STREAM_DATA,
        payload: {
            channel,
            streamKey,
            data,
        },
    };
}

export interface StreamHandleStreamErrorActionPayload
    extends StreamActionPayload {
    error: StreamError;
}

export type StreamHandleStreamErrorAction = Action<
    StreamActionType.STREAM_HANDLE_STREAM_ERROR,
    StreamHandleStreamErrorActionPayload
>;

export function createHandleStreamErrorAction(
    channel: ChannelDestination,
    streamKey: string,
    error: StreamError
): StreamHandleStreamErrorAction {
    return {
        type: StreamActionType.STREAM_HANDLE_STREAM_ERROR,
        payload: omitUndefined({
            channel,
            streamKey,
            error,
        }),
    };
}

export type ReplaceAllStreamDataAction = Action<
    StreamActionType.REPLACE_ALL_STREAM_DATA,
    StreamedDataState
>;

export function createReplaceAllStreamDataPayload(
    data: StreamedDataState
): ReplaceAllStreamDataAction {
    return { type: StreamActionType.REPLACE_ALL_STREAM_DATA, payload: data };
}

export type StreamAction<TDataElement> =
    | ClearStreamDataAction
    | RemoveStreamDataAction
    | SetStreamDataAction
    | StreamMessageReceiveAction<TDataElement>
    | StreamMessageReceiveBatchAction<TDataElement>
    | StreamHandleStreamErrorAction;
